// Sprint 0083 — Punching Machine logs viewer.
// All duty-hour math comes from backend; UI is display-only.

import { useState } from 'react'
import {
  Alert,
  App, Button, Card, Col, DatePicker, Empty, Form, Row, Select, Space, Statistic, Tag, Typography,
} from 'antd'
import { ReloadOutlined, TeamOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { punchesApi, type PunchFeedRow } from '@/features/hr/manpowerApi'
import { employeesApi } from '@/features/hr/api'
import DataTable from '@/shared/components/DataTable'
import PageHeader from '@/shared/components/PageHeader'

const { Text } = Typography

const DIRECTION_COLOR: Record<string, string> = {
  IN: 'green',
  OUT: 'blue',
  UNKNOWN: 'default',
}

export default function PunchLogsPage() {
  const { message } = App.useApp()
  const [employeeId, setEmployeeId] = useState<string | undefined>()
  const [date, setDate] = useState<string>(dayjs().format('YYYY-MM-DD'))

  const { data: employees } = useQuery({
    queryKey: ['employees-all'],
    queryFn: () => employeesApi.list({ page: 1, size: 1000 }),
  })

  const { data: punches, isLoading, refetch } = useQuery({
    queryKey: ['punches', employeeId, date],
    queryFn: () => punchesApi.list(employeeId!, { start: date, end: date }),
    enabled: !!employeeId,
  })

  const { data: dutyHours } = useQuery({
    queryKey: ['duty-hours', employeeId, date],
    // Omit roster_hours — backend derives from shift_rosters / default_shift / 8h fallback
    queryFn: () => punchesApi.dutyHours(employeeId!, { on_date: date }),
    enabled: !!employeeId,
  })

  // Cross-employee feed shown when no specific employee is picked.
  // This is the default view — answers "who punched today?" without
  // making the user select each worker one-by-one.
  const { data: feed, isLoading: feedLoading, refetch: refetchFeed } = useQuery({
    queryKey: ['punches-feed', date],
    queryFn: () => punchesApi.feed({ on_date: date, limit: 1000 }),
    enabled: !employeeId,
  })

  const empName = (id: string) =>
    employees?.items.find((e) => e.id === id)?.emp_name ?? '—'
  const empCode = (id: string) =>
    employees?.items.find((e) => e.id === id)?.emp_code ?? '—'

  // ── Anomaly detection (display-only flags; backend remains source of truth) ──
  type Punch = { id: string; punch_time: string; direction: string; device_id?: string | null }
  const punchList = (punches ?? []) as Punch[]
  const directions = punchList.map((p) => p.direction)
  const ins = directions.filter((d) => d === 'IN').length
  const outs = directions.filter((d) => d === 'OUT').length
  const missingOut = ins > outs
  const oddPunches = punchList.length % 2 === 1
  // Duplicate within 60 seconds
  const sorted = [...punchList].sort(
    (a, b) => new Date(a.punch_time).getTime() - new Date(b.punch_time).getTime(),
  )
  const duplicateIds = new Set<string>()
  for (let i = 1; i < sorted.length; i++) {
    const gap = (new Date(sorted[i].punch_time).getTime()
      - new Date(sorted[i - 1].punch_time).getTime()) / 1000
    if (gap < 60 && sorted[i].direction === sorted[i - 1].direction) {
      duplicateIds.add(sorted[i].id)
    }
  }

  return (
    <div>
      <PageHeader
        title="Punching Machine Logs"
        subtitle="Raw punches + backend-calculated duty hours. Duty hours = system value (do not recalculate in UI)."
        extra={
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isLoading}>
            Refresh
          </Button>
        }
      />

      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <Form.Item label="Employee" style={{ marginBottom: 0 }}>
            <Select
              showSearch
              allowClear
              style={{ width: 320 }}
              placeholder="Pick employee (or leave empty for all)"
              value={employeeId}
              options={(employees?.items ?? []).map((e) => ({
                label: `${e.emp_name} (${e.emp_code})`,
                value: e.id,
              }))}
              filterOption={(input, opt) =>
                ((opt?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
              }
              onChange={setEmployeeId}
            />
          </Form.Item>
          <Form.Item label="Date" style={{ marginBottom: 0 }}>
            <DatePicker
              value={dayjs(date)}
              onChange={(d) => setDate(d?.format('YYYY-MM-DD') ?? dayjs().format('YYYY-MM-DD'))}
            />
          </Form.Item>
          {employeeId && (
            <Form.Item label=" " colon={false} style={{ marginBottom: 0 }}>
              <Button
                icon={<TeamOutlined />}
                onClick={() => setEmployeeId(undefined)}
              >
                Show all workers
              </Button>
            </Form.Item>
          )}
        </Space>
      </Card>

      {/* Per-employee no-data state — friendlier than 0 hrs / -8.00 variance */}
      {employeeId && dutyHours && dutyHours.punches_count === 0 && !isLoading && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={`No punches for ${empName(employeeId)} (${empCode(employeeId)}) on ${dayjs(date).format('DD MMM YYYY')}`}
          description={
            <Space direction="vertical" size={4} style={{ fontSize: 13 }}>
              <span>This usually means one of:</span>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>The worker was <b>off-duty / on leave</b> that day</li>
                <li>The biometric device wasn't <b>enrolled</b> with this worker's fingerprint yet</li>
                <li>The shift hasn't started or already ended on the selected date</li>
              </ul>
              <span>
                Click <b>Show all workers</b> above to see who DID punch on this date.
              </span>
            </Space>
          }
        />
      )}

      {employeeId && dutyHours && dutyHours.punches_count > 0 && (
        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
          <Col xs={12} md={6}>
            <Card>
              <Statistic
                title="Actual duty hours"
                value={dutyHours.actual_hours}
                suffix="hrs"
                precision={2}
              />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card>
              <Statistic
                title="Roster hours"
                value={dutyHours.roster_hours}
                suffix="hrs"
                precision={1}
              />
              {dutyHours.roster_source && (
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                  Source: {dutyHours.roster_source}
                </Text>
              )}
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card>
              <Statistic
                title="Variance vs roster"
                value={dutyHours.variance_hours}
                suffix="hrs"
                precision={2}
                valueStyle={{
                  color: dutyHours.variance_hours < 0
                    ? '#cf1322'
                    : dutyHours.variance_hours > 0.5
                      ? '#1677ff'
                      : '#52c41a',
                }}
              />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card>
              <Statistic
                title="Punches today"
                value={dutyHours.punches_count}
              />
            </Card>
          </Col>
        </Row>
      )}

      {employeeId && punchList.length > 0 && (oddPunches || missingOut || duplicateIds.size > 0) && (
        <Card size="small" style={{ marginBottom: 12, background: '#fff7e6' }}>
          <Space direction="vertical" size={4}>
            {missingOut && (
              <Text type="warning">
                ⚠️ Possible missing OUT punch — {ins} IN vs {outs} OUT
              </Text>
            )}
            {oddPunches && !missingOut && (
              <Text type="warning">
                ⚠️ Odd number of punches ({punchList.length}) — incomplete IN/OUT pair
              </Text>
            )}
            {duplicateIds.size > 0 && (
              <Text type="warning">
                ⚠️ {duplicateIds.size} duplicate punch(es) within 60 seconds — flagged in red below
              </Text>
            )}
          </Space>
        </Card>
      )}

      {employeeId && punchList.length > 0 && (
        <DataTable
          rowKey="id"
          loading={isLoading}
          dataSource={sorted}
          columns={[
            {
              title: 'Time', dataIndex: 'punch_time', key: 'time', width: 200,
              render: (v: string) => dayjs(v).format('DD MMM YYYY HH:mm:ss'),
            },
            {
              title: 'Direction', dataIndex: 'direction', key: 'dir', width: 120,
              render: (v: string) => <Tag color={DIRECTION_COLOR[v] ?? 'default'}>{v}</Tag>,
            },
            { title: 'Device', dataIndex: 'device_id', key: 'device' },
            {
              title: 'Flag', key: 'flag', width: 120,
              render: (_: unknown, r: Punch) =>
                duplicateIds.has(r.id) ? <Tag color="red">DUPLICATE</Tag> : null,
            },
          ]}
        />
      )}

      {!employeeId && (
        <Card
          size="small"
          title={
            <Space>
              <span>All Punches on {dayjs(date).format('DD MMM YYYY')}</span>
              {feed && (
                <Tag color="blue">
                  {feed.length} punches · {new Set(feed.map((f) => f.employee_id)).size} workers
                </Tag>
              )}
            </Space>
          }
          extra={
            <Button
              icon={<ReloadOutlined />}
              size="small"
              onClick={() => refetchFeed()}
              loading={feedLoading}
            >
              Refresh
            </Button>
          }
        >
          <DataTable
            rowKey="id"
            loading={feedLoading}
            dataSource={feed ?? []}
            columns={[
              {
                title: 'Time',
                dataIndex: 'punch_time',
                key: 'time',
                width: 150,
                render: (v: string) => dayjs(v).format('HH:mm:ss'),
                sorter: (a: PunchFeedRow, b: PunchFeedRow) =>
                  new Date(a.punch_time).getTime() - new Date(b.punch_time).getTime(),
                defaultSortOrder: 'descend' as const,
              },
              {
                title: 'Worker',
                key: 'worker',
                render: (_: unknown, r: PunchFeedRow) => (
                  <Space size={4} direction="vertical" style={{ lineHeight: 1.2 }}>
                    <Text strong>{r.emp_name ?? '—'}</Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {r.emp_code} · bio {r.biometric_id}
                    </Text>
                  </Space>
                ),
              },
              {
                title: 'Department',
                dataIndex: 'department_name',
                key: 'dept',
                width: 180,
                render: (v: string | null) => v ?? <Text type="secondary">—</Text>,
              },
              {
                title: 'Direction',
                dataIndex: 'direction',
                key: 'dir',
                width: 110,
                render: (v: string) => <Tag color={DIRECTION_COLOR[v] ?? 'default'}>{v}</Tag>,
                filters: [
                  { text: 'IN', value: 'IN' },
                  { text: 'OUT', value: 'OUT' },
                  { text: 'UNKNOWN', value: 'UNKNOWN' },
                ],
                onFilter: (val: boolean | React.Key, r: PunchFeedRow) => r.direction === val,
              },
              {
                title: 'Location',
                dataIndex: 'location',
                key: 'loc',
                width: 180,
                render: (v: string | null) => v ?? <Text type="secondary">—</Text>,
              },
              {
                title: 'Device',
                dataIndex: 'device_id',
                key: 'device',
                width: 140,
                render: (v: string | null) => v ? <code style={{ fontSize: 11 }}>{v}</code> : '—',
              },
            ]}
          />
          {(!feed || feed.length === 0) && !feedLoading && (
            <div style={{ textAlign: 'center', padding: 24 }}>
              <Text type="secondary">
                No punches on {dayjs(date).format('DD MMM YYYY')}. Pick a different date or check
                that the biometric device is online.
              </Text>
            </div>
          )}
        </Card>
      )}

      {employeeId && (
        <div style={{ marginTop: 16 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Showing {empName(employeeId)} ({empCode(employeeId)}) on {date}.
            Duty hours computed server-side from paired IN/OUT punches.
            Variance ≠ overtime — see OT report for paid OT.
          </Text>
        </div>
      )}
    </div>
  )
}
